/**
 * Replies API - Update Operations
 *
 * Updating existing replies.
 */

import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../firebaseConfig";
import { ensureFreshToken } from "../authUtils";

/**
 * Updates the text of an existing reply, with ownership verification.
 * Prefers UID-based ownership; falls back to username comparison for legacy
 * replies that were created before `userId` was stored.
 *
 * @param {string} replyId
 * @param {{ text: string, username?: string }} replyData
 * @returns {Promise<true>}
 */
export const updateReply = async (replyId, replyData) => {
  try {
    await ensureFreshToken();
    const currentUid = auth?.currentUser?.uid || null;
    if (!currentUid) {
      throw new Error("You must be logged in to update a reply");
    }

    const replyRef = doc(db, "replies", replyId);

    // ── Phase 1: Data Sanitization (Source of Truth) ──
    const trimmedText = (replyData.text || "").trim();

    if (!trimmedText) {
      throw new Error("Reply text cannot be empty");
    }
    if (trimmedText.length > 2000) {
      throw new Error(
        `Reply exceeds maximum length of 2000 characters (got ${trimmedText.length})`,
      );
    }

    // Client-side ownership check before writing
    const replySnap = await getDoc(replyRef);
    if (!replySnap.exists()) {
      throw new Error("Reply does not exist");
    }
    const existingData = replySnap.data();
    if (existingData.userId) {
      if (existingData.userId !== currentUid) {
        throw new Error("You do not have permission to update this reply");
      }
    } else {
      // Legacy username fallback
      const replyUsername = (existingData.username || "").trim().toLowerCase();
      const clientUsername = (replyData.username || "").trim().toLowerCase();
      if (!replyUsername || replyUsername !== clientUsername) {
        throw new Error("You do not have permission to update this reply");
      }
    }

    await updateDoc(replyRef, {
      text: trimmedText,
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error("Error updating reply:", error);
    throw error;
  }
};
